"use client";
import { useRouter } from "next/navigation";
import { FormError, FormGroup, Input, Label, useClientSide } from "cy-fe";
import { PhoneNumber } from "cy-api/simple-payments/common-models";
import { urls } from "@/urls";
import { createLoggerClient } from "@/lib/logging/client";
import {
  getCurrentStep as currentStepSelector,
  useRegisterStore,
  PersonalInfoInputs,
  Step,
  stepFlow,
  AccountType,
  AccountLimit,
  AccountOptionsPage,
  OtpPage,
  DonePage,
  MobileNumberPage,
  PersonalInfoPage,
  PasswordPage,
  NationalIdProps,
} from "cy-remittance/registration";
import { notifyError } from "cy-remittance/registration/notifyError";
import { verifyRegistrationIdClient } from "cy-api/simple-payments/customer/verifyRegistrationIdClient";
import { AccountCategory } from "cy-api/simple-payments/account-categories/models";
import { createCustomerClient } from "cy-api/simple-payments/customer/createCustomerClient";
import { ClientRedirect } from "cy-ui/components/ClientRedirect";
import * as StepWizard from "cy-ui/components/StepWizard";
import {
  AccountLimits,
  accountLimits,
  AccountTypes,
  accountTypes,
} from "./models";
import { renderAccountType } from "./renderAccountType";
import { useSaNationalIdValidator } from "cy-ui/south-african-national-id";
import { renderAccountLimits } from "./renderAccountLimits";
import { registrationText } from "@/constants";
import {
  IdentificationTypeName,
  findIdentificationTypeByName,
} from "cy-remittance/registration/identification";
import { IdentificationType } from "cy-api/simple-payments/ref-data/models";
import { MarketingInfo } from "./MarketingInfo";
import { RegistrationInitializer } from "cy-remittance/registration/referral/RegistrationInitializer";
import { useEffect } from "react";

const logger = createLoggerClient("registration-page");

export function PageClient({
  step,
  accountCategories,
  identificationTypes,
}: {
  step: Step;
  accountCategories: AccountCategory[];
  identificationTypes: IdentificationType[];
}) {
  step = Step.parse(step);
  const isClientSide = useClientSide();
  const currentStep = useRegisterStore(currentStepSelector);
  const state = useRegisterStore();
  const router = useRouter();

  const stepHook = StepWizard.useStepWizard(step, Step.options);

  const isSouthAfricanIdHolder =
    state.accountType?.id === (AccountTypes.SouthAfrican as string);
  const hasAccountLimit =
    state.accountType?.id === AccountTypes.ForeignNational;

  const redirectToStart =
    stepFlow.indexOf(step) > stepFlow.indexOf(currentStep || "start");
  const redirectToDone = currentStep === "done" && step !== "done";

  useEffect(() => {
    if (!redirectToStart) {
      return;
    }
    logger.warn("Invalid registration step detected, redirecting to start", {
      step,
      currentStep,
      stepFlowPosition: stepFlow.indexOf(step),
      currentStepPosition: stepFlow.indexOf(currentStep || "start"),
      userAction: "navigation_prevention",
    });
  }, [redirectToStart, step, currentStep]);

  if (redirectToStart) {
    return <ClientRedirect href={urls.register.start.href} />;
  }
  if (redirectToDone) {
    return <ClientRedirect href={urls.register.done.href} />;
  }

  async function requestConfirmationOtp(): Promise<void> {
    const [_, error] = await verifyRegistrationIdClient({
      mobile: state.mobile,
    });

    if (error) {
      notifyError(error);
      return;
    }

    return Promise.resolve();
  }

  function mobileContinueHandler(mobile: PhoneNumber) {
    state.setMobile(mobile);
    router.push(urls.register.accountType.href);
  }

  function accountTypeContinueHandler(accountType: AccountType<AccountTypes>) {
    if (state.accountType !== accountType) {
      state.setAccountLimit(undefined);
    }
    state.setAccountType(accountType);

    if (accountType.id === AccountTypes.ForeignNational) {
      router.push(urls.register.accountLimit.href);
    } else {
      router.push(urls.register.personal.href);
    }
  }

  function accountLimitContinueHandler(accountLimit: AccountLimit) {
    state.setAccountLimit(accountLimit);
    router.push(urls.register.personal.href);
  }

  async function passwordSubmitHandler(args: {
    password: string;
  }): Promise<void> {
    state.setPassword(args.password);

    await requestConfirmationOtp();

    router.push(urls.register.otp.href);
  }

  async function otpSubmitHandler({ otp }: { otp: string }): Promise<void> {
    const label = state.accountType?.label || state.accountLimit?.label;
    const categoryId = accountCategories.find(
      (category) => category.name === label,
    )?.id;

    if (state.personalInfo == null) {
      router.push(urls.register.personal.href);
      return;
    }
    const yearStr = state.personalInfo.dateOfBirth.year
      .toString()
      .padStart(4, "0");
    const monthStr = state.personalInfo.dateOfBirth.month
      .toString()
      .padStart(2, "0");
    const dayStr = state.personalInfo.dateOfBirth.day
      .toString()
      .padStart(2, "0");
    const idType = findIdentificationTypeByName(
      identificationTypes,
      IdentificationTypeName.SouthAfricanId,
      logger,
    );
    if (idType == null) {
      notifyError(null, {
        defaultMessage:
          "Unable to load identification type information. Please try again later.",
      });
      return;
    }
    const dateStr = `${yearStr}-${monthStr}-${dayStr}`;

    // As per CW-176, if any are checked then marketing is consented
    const marketingConsent = Object.values(state.marketingConsents || {}).some(
      (x) => x,
    );
    const [_, error] = await createCustomerClient({
      otp,
      terms_and_conditions_accepted: true,
      mobile: state.mobile,
      password: state.password || "",
      category_id: categoryId,
      nationality_id: state.personalInfo.nationality || undefined,
      occupation_id: state.personalInfo.occupation || undefined,
      source_of_funds_id: state.personalInfo.sourceofFunds || undefined,
      email: state.personalInfo.email || undefined,
      identification: state.personalInfo.nationalId
        ? {
            number: state.personalInfo.nationalId,
            type_id: idType.id,
            country_code: "ZA",
            expiry_date: null,
            issue_date: null,
          }
        : undefined,
      name: {
        first_name: state.personalInfo.firstName || null,
        middle_name: state.personalInfo.middleName || null,
        last_name: state.personalInfo.lastName || null,
      },
      gender: state.personalInfo.gender,
      birth_date: dateStr,
      marketing_accepted: marketingConsent,
      affiliate_token: state.referralCode || undefined,
    });

    if (error) {
      logger.error("Unable to complete registration", {}, error);
      notifyError(error, {
        defaultMessage:
          "Unable to complete registration. Please try again later.",
      });
      return;
    }

    state.setOtpConfirmed();
    router.push(urls.register.done.href);
    return Promise.resolve();
  }

  function personalInfoSubmitHandler(args: PersonalInfoInputs) {
    state.setPersonalInfo({ personalInfo: args });
    router.push(urls.register.marketing.href);
  }

  function marketingInfoHandler(args: {
    marketingConsents: Record<string, boolean>;
    referralCode?: string;
  }) {
    state.setMarketingConsents(args.marketingConsents);
    if (args.referralCode !== undefined) {
      state.setReferralCode(args.referralCode);
    }
    router.push(urls.register.password.href);
  }

  function getSection() {
    return (
      <>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("start")}
        >
          <MobileNumberPage
            header={registrationText.createAccountHeader}
            signinUrl={urls.signin.href}
            onContinue={mobileContinueHandler}
            initialMobile={{ isd: "27", number: "" }}
            postLoginUrl={urls.home.href}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("accountType")}
        >
          <AccountOptionsPage
            options={accountTypes}
            backUrl={urls.register.start.href}
            renderOption={renderAccountType}
            onContinue={accountTypeContinueHandler}
            header="Account type"
            subHeader="Select your ID type"
            initialOption={state.accountType as AccountType<AccountTypes>}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("accountLimit")}
        >
          <AccountOptionsPage<AccountLimit<AccountLimits>>
            backUrl={urls.register.accountType.href}
            onContinue={accountLimitContinueHandler}
            options={accountLimits}
            renderOption={renderAccountLimits}
            initialOption={state.accountLimit as AccountLimit<AccountLimits>}
            header="Account type"
            subHeader="Select your transfer limit"
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("personal")}
        >
          <PersonalInfoPage
            backUrl={
              hasAccountLimit
                ? urls.register.accountLimit.href
                : urls.register.accountType.href
            }
            initial={state.personalInfo || {}}
            occupations={state.occupations || []}
            nationalities={state.nationalities || []}
            sourceOfFunds={state.sourceOfFunds || []}
            onSubmit={personalInfoSubmitHandler}
            NationalId={isSouthAfricanIdHolder ? NationalId : undefined}
            hasAccountLimit={hasAccountLimit}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("marketing")}
        >
          <MarketingInfo
            initial={state.marketingConsents}
            initialReferralCode={state.referralCode}
            onSubmit={marketingInfoHandler}
            backUrl={urls.register.personal.href}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("password")}
        >
          <PasswordPage
            termsAndConditionsUrl="https://www.cymonz.com"
            backUrl={urls.register.marketing.href}
            onSubmit={passwordSubmitHandler}
            termsAgreementPrefix={registrationText.termsAgreementPrefix}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer {...stepHook.containerProps("otp")}>
          <OtpPage
            backUrl={urls.register.password.href}
            startUrl={urls.register.start.href}
            mobile={state.mobile}
            onSubmit={otpSubmitHandler}
            resendRequest={requestConfirmationOtp}
          />
        </StepWizard.StepTransitionContainer>
        <StepWizard.StepTransitionContainer
          {...stepHook.containerProps("done")}
        >
          <DonePage
            homeUrl={urls.completeRegistration.href}
            loginRequest={{
              password: state.password || "",
              mobile_isd: state.mobile.isd,
              mobile_number: state.mobile.number,
            }}
          />
        </StepWizard.StepTransitionContainer>
      </>
    );
  }

  if (!isClientSide) {
    return null;
  }
  return (
    <div className="">
      <RegistrationInitializer />
      {getSection()}
    </div>
  );
}

export function NationalId(props: NationalIdProps) {
  const form = props.form;
  const setValue = form.setValue;

  const { createCallbacks } = useSaNationalIdValidator();
  const saIdCallbacks = createCallbacks({
    trigger: props.form.trigger,
    setGender: (gender: "Male" | "Female") => {
      setValue("gender", gender);
    },
    setDateOfBirth: (dob) => {
      setValue("dateOfBirth", dob);
    },
    hasValidationError: !!form.formState.errors.nationalId,
  });
  return (
    <FormGroup controller={{ control: form.control, name: "nationalId" }}>
      <Label>South African Id</Label>
      <Input
        vSize="lg"
        {...form.register("nationalId", {
          validate: saIdCallbacks.validate,
          onBlur: saIdCallbacks.onBlur,
        })}
      />
      <FormError />
    </FormGroup>
  );
}
